<template>
  <v-app>
    <v-expand-transition>
      <v-app-bar app class="px-md-6" v-if="showTopNav">
        <h1
          class="font-weight-bold flex-fill text-truncate pr-2"
          :class="{ 'text-h4': !isMobile, 'text-h5': isMobile }"
        >
          {{ surveyTitle }}
        </h1>
        <help-menu color="primary"></help-menu>
      </v-app-bar>
    </v-expand-transition>
    <v-main>
      <v-navigation-drawer
        app
        :value="showSideNav"
        color="primary"
        width="250px"
      >
        <template v-slot:prepend>
          <v-card-text class="white--text">
            <h4 class="text-h3 font-weight-bold mb-2" :class="surveyTitleClass">
              {{ surveyTitle }}
            </h4>
            <div class="white mt-3 mb-4" style="height: 5px"></div>
          </v-card-text>
        </template>
        <template v-slot:append>
          <v-card-text class="white--text">
            <h6 class="text-subtitle-1 font-weight-bold mb-1">Need Help?</h6>
            <p>If you need assistance you can contact customer support.</p>
            <help-block linkClass="white--text" />
          </v-card-text>
        </template>
      </v-navigation-drawer>
      <v-slide-y-transition>
        <div v-if="showForm" class="d-flex h-100">
          <div class="flex-fill pt-6">
            <survey-steps
              class="px-10 py-6"
              v-model="pageIndex"
              :pages="pages"
              :validate="onValidate"
              :formData="formData"
              :loading="loading"
              v-if="pages.length > 1"
            ></survey-steps>
            <v-scroll-x-transition tag="div" mode="out-in">
              <template v-for="(page, i) in pages">
                <div :key="page.key" v-if="i === pageIndex">
                  <survey-page-title
                    :title="page.displayName"
                    :description="page.description"
                    :pageCount="pages.length"
                    :pageIndex="pageIndex"
                  ></survey-page-title>
                  <v-card-text class="px-md-12 py-md-9 px-6 py-4">
                    <template v-for="question in pageQuestions">
                      <checklist-question
                        ref="pageQuestion"
                        v-if="question.type === questionTypes.checklist"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></checklist-question>
                      <text-block
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.textBlock"
                        :question="question"
                        :key="question.key"
                        class="mb-16"
                      ></text-block>
                      <text-field-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.text"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></text-field-question>
                      <table-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.table"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></table-question>
                      <radio-list-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.radio"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        :disabled="loading"
                        class="mb-16"
                      ></radio-list-question>
                      <ranked-choice-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.rankedChoice"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        :disabled="loading"
                        class="mb-16"
                      ></ranked-choice-question>
                    </template>
                  </v-card-text>
                </div>
              </template>
            </v-scroll-x-transition>
            <survey-page-actions
              class="px-md-12 px-6 pb-md-8 pb-6"
              v-model="pageIndex"
              :pages="pages"
              :pageErrors="pageErrors"
              :validate="onValidate"
              :formData="formData"
              :loading="loading"
              @submit="onSave"
            ></survey-page-actions>
          </div>
        </div>
      </v-slide-y-transition>
    </v-main>

    <v-dialog :value="!showForm" persistent width="300">
      <v-card>
        <v-card-text class="pt-5">
          <Validation-Observer
            ref="codeObserver"
            tag="form"
            @submit.prevent="onSubmitCode"
          >
            <v-alert
              :value="errorMessage != null"
              class="mb-3"
              color="red"
              type="error"
              border="left"
              transition="slide-y-transition"
              >{{ errorMessage }}
            </v-alert>
            <div class="text-h6 text-center mb-2 text--primary">
              Enter Your Access Code
            </div>
            <Validation-Provider rules="required" v-slot="{ errors }">
              <v-text-field
                v-model="formCode"
                placeholder="Access Code"
                outlined
                :filled="loading"
                :error-messages="errors"
                :hide-details="errors.length == 0"
                class="text-center"
                :disabled="loading"
              ></v-text-field>
            </Validation-Provider>
            <v-btn
              type="submit"
              block
              color="primary"
              large
              class="mt-3"
              :loading="loading"
            >
              Get Started
            </v-btn>

            <v-alert
              :value="code && code.used"
              class="mt-4 mb-0 text-center"
              color="green white--text"
              border="top"
              transition="slide-y-transition"
              >Your response has been saved!
            </v-alert>
          </Validation-Observer>
        </v-card-text>
      </v-card>
    </v-dialog>
    <notifications></notifications>
  </v-app>
</template>

<script>
import surveyService from "../services/surveyService";

import Notifications from "../components/Notifications.vue";

import SurveyPageTitle from "../components/Survey.PageTitle.vue";
import SurveySteps from "../components/Survey.Steps.vue";
import SurveyPageActions from "../components/Survey.PageActions.vue";

import ChecklistQuestion from "../components/Survey.Question.Checklist.vue";
import TableQuestion from "../components/Survey.Question.Table.vue";
import TextBlock from "../components/Survey.Question.TextBlock.vue";
import TextFieldQuestion from "../components/Survey.Question.TextField.vue";
import RadioListQuestion from "../components/Survey.Question.RadioList.vue";
import RankedChoiceQuestion from "../components/Survey.Question.RankedChoice.vue";

import {
  questionTypes,
  showQuestion,
  surveyStatus,
  accessKeyUseTypes,
} from "../constants/Survey";
import HelpBlock from "../components/HelpBlock.vue";
import HelpMenu from "../components/HelpMenu.vue";
import {
  dtFromServerString,
  formattedTimestamp,
} from "../utilities/dataUtilities";

export default {
  name: "SurveyForm",
  components: {
    Notifications,
    SurveyPageTitle,
    SurveySteps,
    SurveyPageActions,
    ChecklistQuestion,
    TableQuestion,
    TextBlock,
    TextFieldQuestion,
    RadioListQuestion,
    RankedChoiceQuestion,
    HelpBlock,
    HelpMenu,
  },
  head: {
    title: {
      inner: "Survey",
      complement: " ",
      separator: " ",
    },
  },
  data: () => ({
    pendingStateChanges: 0,
    pageYOffset: 0,
    shrinkThreshold: 70,
    errorMessage: null,
    userCode: "",
    code: null,
    survey: null,
    pageIndex: 0,
    formData: [],
    pageErrors: [],
    started: null,
  }),
  computed: {
    showForm() {
      return this.code && this.code.active && !this.code.used;
    },
    showSideNav() {
      return !this.isMobile && this.survey;
    },
    showTopNav() {
      return this.isMobile && this.survey;
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    appbarHeight() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 148
        : 70;
    },
    logoWidth() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 128
        : 48;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    surveyId() {
      return this.$route.params.id;
    },
    tenantId() {
      return this.$route.params.tenantId;
    },
    surveyTitle() {
      return this.survey?.title ?? "";
    },
    surveyTitleClass() {
      let len = this.surveyTitle.length;
      if (len <= 30) {
        return "text-h3";
      }

      if (len <= 75) {
        return "text-h4";
      }

      if (len <= 150) {
        return "text-h5";
      }

      return "text-h6";
    },
    questionTypes() {
      return questionTypes;
    },
    pages() {
      return this.survey ? this.survey.categories : [];
    },
    pageQuestions() {
      let results = [];

      this.pages[this.pageIndex].questions.forEach((x) => {
        if (showQuestion(x, this.formData)) results.push(x);
      });

      return results;
    },
    testing() {
      return this.$route.query.testing == "true";
    },
    formCode: {
      set(val) {
        this.userCode = val.toUpperCase();
      },
      get() {
        return this.userCode;
      },
    },
    multiUseCodes() {
      return this.survey?.accessKeyUseType == accessKeyUseTypes.multiple;
    },
  },
  watch: {
    pageIndex() {
      this.$vuetify.goTo(document.querySelector("#surveySteps"), {
        duration: 0,
      });
    },
  },
  methods: {
    loadSurvey() {
      this.pendingStateChanges++;

      surveyService
        .get(this.tenantId, this.surveyId)
        .then(
          (response) => {
            this.survey = response;
          },
          (error) => {
            this.errorMessage =
              error.errorMessage ?? "An unexpected error occurred.";
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    async onSubmitCode() {
      let isValid = await this.$refs.codeObserver.validate();
      if (!isValid) return;

      let startDate = dtFromServerString(this.survey.startDate);
      let endDate = dtFromServerString(this.survey.endDate);
      let now = new Date();

      if (this.survey.status == surveyStatus.published.key) {
        if (now < startDate) {
          this.errorMessage = "The survey hasn't started.";
          return;
        }

        if (now > endDate) {
          this.errorMessage = "The survey has ended.";
          return;
        }
      } else if (this.survey.status == surveyStatus.archived.key) {
        this.errorMessage = "The survey has ended.";
        return;
      } else if (
        this.survey.status == surveyStatus.draft.key &&
        !this.testing
      ) {
        this.errorMessage =
          "The survey is under construction. Please try again later.";
        return;
      } else if (this.survey.status == surveyStatus.draft.key && this.testing) {
        // skip checks.
      } else {
        this.errorMessage =
          "An unexpected error occurred. Please try again later.";
        console.error("Invalid survey status!");
        return;
      }

      this.errorMessage = null;

      this.pendingStateChanges++;
      surveyService
        .getCodeDetail(this.tenantId, this.surveyId, this.userCode)
        .then(
          (response) => {
            if(!response.active){
              this.errorMessage = "Code is not active";
            }
            if (this.multiUseCodes) response.used = false;
            this.code = response;
            this.$nextTick(this.setFormData);
            this.started = new Date();
          },
          (error) => {
            console.warn(error);
            if (error.statusCode === 404) {
              this.errorMessage = "Invalid Access Code";
            } else {
              this.errorMessage =
                "An unexpected error occurred. Please try again later.";
            }
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    setFormData() {
      let formData = [];

      this.survey.categories.forEach((cat) =>
        cat.questions.forEach((question) => addFormDataField(question))
      );

      this.formData = formData;

      function addFormDataField(question) {
        switch (question.type) {
          case questionTypes.table:
            question.children.forEach((child) => {
              formData.push({ key: child.key, value: null });
            });
            break;
          default:
            formData.push({ key: question.key, value: null });
            break;
        }
      }
    },
    onValidate() {
      let targets = this.$refs.pageQuestion;
      let focusedFirstErr = false;

      let errors = [];

      if (Array.isArray(targets)) {
        targets.forEach((x) => {
          if (x.errors) errors = errors.concat(x.errors);
          if (x.validate) {
            let isValid = x.validate();
            if (!isValid && !focusedFirstErr) {
              focusedFirstErr = true;

              let scrollPos = Math.floor(window.pageYOffset);
              if (
                scrollPos ==
                document.querySelector("body").offsetHeight - window.innerHeight
              )
                window.scrollTo({
                  top: scrollPos - 1,
                });

              this.$vuetify.goTo(x.$el, {
                duration: document.querySelector("body").offsetHeight * 0.2,
                easing: "easeInOutCubic",
              });
            }
          }
        });
      }

      this.pageErrors = errors;
      return errors.length === 0;
    },
    onSave() {
      if (!this.onValidate()) return;

      let response = {
        accessCode: this.userCode,
        started: formattedTimestamp(this.started),
        completed: formattedTimestamp(Date.now()),
        answers: this.formData.slice().map((x) => ({
          key: x.key,
          value: typeof x.value != "string" ? JSON.stringify(x.value) : x.value,
        })),
        isTest: this.$route.query.testing == "true",
      };

      this.pendingStateChanges++;
      surveyService
        .saveResponse(this.tenantId, this.surveyId, response)
        .then(
          () => {
            this.code.used = true;
            this.userCode = null;
            this.pageIndex = 0;
            this.setFormData();
            this.$refs.codeObserver.reset();
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;
    this.$store.commit("setTheme", "adi");

    let favicon = document.querySelector("#favicon");
    favicon.href = "favicon.ico";
    // Load Survey Data
    this.loadSurvey();
        
    for(let key in this.$route.query) {
      if (key.toLowerCase() === 'code') this.userCode = this.$route.query[key];
    }
  },
};
</script>

<style lang="scss">
html {
  font-size: 1rem;
}

#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-icon.v-icon.overlay-back {
  font-size: 24px !important;
}

.v-input {
  &.text-center input {
    text-align: center;
  }

  input:focus::placeholder {
    color: #fff;
  }
}

.h-100 {
  min-height: 100%;
}
</style>
